import React, { useState, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage, injectIntl } from 'react-intl'
import {
  MdHistory,
  MdDelete,
  MdAdd,
  MdExpandMore,
  MdExpandLess,
} from 'react-icons/md'
import {
  loadConversation,
  deleteConversation,
  clearChatHistory,
} from '../actions'
import {
  selectConversations,
  selectConversationsLoading,
  selectConversationId,
  selectChatLoading,
  selectDeletingConversationId,
} from '../selectors'
import messages from '../messages'
import styles from '../styles.module.scss'
import { filterConversationsForContext } from '../utils'

const ConversationHistory = ({ scopeCode, companyCode, intl }) => {
  const [isOpen, setIsOpen] = useState(false)
  const dispatch = useDispatch()
  const conversations = useSelector(selectConversations)
  const conversationsLoading = useSelector(selectConversationsLoading)
  const activeConversationId = useSelector(selectConversationId)
  const isChatLoading = useSelector(selectChatLoading)
  const deletingConversationId = useSelector(selectDeletingConversationId)
  const containerRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  // Every mutating action below is blocked while an answer is streaming. The saga is not tied to
  // the UI: clearing or deleting mid-stream leaves it running, and its later success restores the
  // conversation the user just tried to leave — or fails on the backend when the assistant message
  // is persisted into a conversation that has since been deleted.
  const handleNewConversation = () => {
    if (isChatLoading) return
    dispatch(clearChatHistory())
    setIsOpen(false)
  }

  const handleSelectConversation = (id) => {
    if (id !== activeConversationId && !isChatLoading) {
      dispatch(loadConversation(id, scopeCode))
      setIsOpen(false)
    }
  }

  // Only the active conversation races the stream; deleting some other one is harmless.
  const isDeleteBlocked = (id) => isChatLoading && id === activeConversationId

  const handleDeleteConversation = (e, id) => {
    e.stopPropagation()
    if (isDeleteBlocked(id)) return
    dispatch(deleteConversation(id, scopeCode))
  }

  const conversationsList = (() => {
    if (!conversations) return []
    const js = conversations.toJS ? conversations.toJS() : conversations
    if (!Array.isArray(js)) return []

    return filterConversationsForContext(js, { companyCode })
  })()

  return (
    <div className={styles.conversationHistoryContainer} ref={containerRef}>
      <button
        className={styles.historyButton}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={intl.formatMessage(messages.conversations)}
        title={intl.formatMessage(messages.conversations)}
      >
        <MdHistory size={18} />
        <FormattedMessage {...messages.conversations} />
        {conversationsLoading ? (
          <span className={styles.historyButtonSpinner} />
        ) : conversationsList.length > 0 ? (
          <span className={styles.conversationCount}>
            {conversationsList.length}
          </span>
        ) : null}
        {isOpen ? <MdExpandLess size={15} /> : <MdExpandMore size={15} />}
      </button>

      {isOpen && (
        <div className={styles.conversationDropdown}>
          <button
            className={styles.newConversationButton}
            onClick={handleNewConversation}
            disabled={isChatLoading}
          >
            <MdAdd size={16} />
            <FormattedMessage {...messages.newConversation} />
          </button>

          {conversationsLoading ? (
            <div className={styles.conversationsLoading}>
              <div className={styles.statusSpinner} />
            </div>
          ) : conversationsList.length === 0 ? (
            <div className={styles.conversationsEmpty}>
              <FormattedMessage {...messages.conversationsEmpty} />
            </div>
          ) : (
            <ul className={styles.conversationsList}>
              {conversationsList.map((conv) => (
                <li
                  key={conv.id}
                  className={`${styles.conversationItem} ${
                    conv.id === activeConversationId
                      ? styles.conversationItemActive
                      : ''
                  }`}
                >
                  {/* A real button, not an onClick on the <li>: the list item is not focusable,
                      so keyboard users had no way to open a previous conversation at all. */}
                  <button
                    type="button"
                    className={styles.conversationItemContent}
                    onClick={() => handleSelectConversation(conv.id)}
                    disabled={isChatLoading}
                    aria-current={
                      conv.id === activeConversationId ? 'true' : undefined
                    }
                  >
                    <span className={styles.conversationTitle}>
                      {conv.title ||
                        intl.formatMessage(messages.untitledConversation)}
                    </span>
                    <span className={styles.conversationMeta}>
                      {intl.formatMessage(messages.conversationMessages, {
                        count: conv.messageCount ?? 0,
                      })}
                      {' · '}
                      {new Date(conv.updatedAt).toLocaleDateString('fi-FI')}
                    </span>
                  </button>
                  <button
                    className={styles.deleteConversationButton}
                    onClick={(e) => handleDeleteConversation(e, conv.id)}
                    aria-label={intl.formatMessage(messages.deleteConversation)}
                    title={intl.formatMessage(messages.deleteConversation)}
                    disabled={
                      deletingConversationId === conv.id ||
                      isDeleteBlocked(conv.id)
                    }
                  >
                    {deletingConversationId === conv.id ? (
                      <span className={styles.historyButtonSpinner} />
                    ) : (
                      <MdDelete size={16} />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

export default injectIntl(ConversationHistory)
